import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface FrequencyPattern {
  pattern: string;
  description: string;
  timing: string;
}

const frequencyPatterns: FrequencyPattern[] = [
  { pattern: "SOS", description: "As needed", timing: "When required" },
  { pattern: "1-0-1", description: "Twice daily", timing: "Morning & Evening" },
  { pattern: "1-1-1", description: "Three times daily", timing: "With meals" },
  { pattern: "1-0-0", description: "Once daily", timing: "Morning only" },
  { pattern: "0-1-0", description: "Once daily", timing: "Lunch only" },
  { pattern: "0-0-1", description: "Once daily", timing: "Evening only" },
  { pattern: "1-1-0", description: "Twice daily", timing: "Morning & Lunch" },
  { pattern: "0-1-1", description: "Twice daily", timing: "Lunch & Evening" },
  { pattern: "1-1-1-1", description: "Four times daily", timing: "Every 6 hours" }
];

interface FrequencyPatternSelectorProps {
  value: string;
  patternValue?: string;
  descriptionValue?: string;
  onChange: (frequency: string, pattern?: string, description?: string) => void;
}

export function FrequencyPatternSelector({
  value,
  patternValue,
  descriptionValue,
  onChange
}: FrequencyPatternSelectorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    
    // Check if the input matches any existing pattern (exact or partial)
    const exactMatch = frequencyPatterns.find(pattern => 
      pattern.description.toLowerCase() === newValue.toLowerCase() ||
      pattern.pattern.toLowerCase() === newValue.toLowerCase() ||
      `${pattern.description} (${pattern.timing})`.toLowerCase() === newValue.toLowerCase()
    );
    
    if (exactMatch) {
      // If it matches exactly, use the full format
      const frequencyText = `${exactMatch.description} (${exactMatch.timing})`;
      console.log('FrequencyPatternSelector: Exact match found, calling onChange with:', {
        frequency: frequencyText,
        pattern: exactMatch.pattern,
        description: exactMatch.description
      });
      onChange(frequencyText, exactMatch.pattern, exactMatch.description);
    } else {
      // For manual input, check if it's a partial match and suggest completion
      const partialMatch = frequencyPatterns.find(pattern => 
        pattern.description.toLowerCase().includes(newValue.toLowerCase()) ||
        newValue.toLowerCase().includes(pattern.description.toLowerCase())
      );
      
      if (partialMatch && newValue.trim().length > 0) {
        // If it's a partial match, complete it with the full format
        const frequencyText = `${partialMatch.description} (${partialMatch.timing})`;
        console.log('FrequencyPatternSelector: Partial match found, completing with:', {
          frequency: frequencyText,
          pattern: partialMatch.pattern,
          description: partialMatch.description
        });
        onChange(frequencyText, partialMatch.pattern, partialMatch.description);
      } else {
        // For manual input, keep as-is but clear pattern data
        console.log('FrequencyPatternSelector: No match, calling onChange with:', {
          frequency: newValue,
          pattern: "",
          description: ""
        });
        onChange(newValue, "", "");
      }
    }
    
    // Show dropdown when typing - filtered if there's text, all if empty
    if (newValue.trim().length > 0) {
      const matchingPatterns = frequencyPatterns.filter(pattern =>
        pattern.pattern.toLowerCase().includes(newValue.toLowerCase()) ||
        pattern.description.toLowerCase().includes(newValue.toLowerCase()) ||
        pattern.timing.toLowerCase().includes(newValue.toLowerCase())
      );
      setShowDropdown(matchingPatterns.length > 0);
      setHighlightedIndex(-1); // Reset highlight when typing
    } else {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const handlePatternSelect = (pattern: FrequencyPattern) => {
    const frequencyText = `${pattern.description} (${pattern.timing})`;
    setInputValue(frequencyText);
    console.log('FrequencyPatternSelector: Pattern selected, calling onChange with:', {
      frequency: frequencyText,
      pattern: pattern.pattern,
      description: pattern.description
    });
    // Ensure we pass the full frequency text as the main frequency value
    onChange(frequencyText, pattern.pattern, pattern.description);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    // Show all patterns when focused (like dose type field)
    if (inputValue.trim().length > 0) {
      setShowDropdown(true);
    } else {
      // Show all patterns when field is empty and focused
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    // Use filtered patterns for keyboard navigation
    const matchingPatterns = frequencyPatterns.filter(pattern =>
      pattern.pattern.toLowerCase().includes(inputValue.toLowerCase()) ||
      pattern.description.toLowerCase().includes(inputValue.toLowerCase()) ||
      pattern.timing.toLowerCase().includes(inputValue.toLowerCase())
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < matchingPatterns.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : matchingPatterns.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < matchingPatterns.length) {
          handlePatternSelect(matchingPatterns[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="frequency-pattern">Frequency Pattern</Label>
      <div className="relative">
        <Input 
          id="frequency-pattern"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder="Type frequency pattern..."
          className="w-full"
        />
        
        {/* Smart dropdown - opens upward, all patterns on focus, filtered on typing */}
        {showDropdown && (
          <div 
            className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
            role="listbox"
            aria-label="Frequency pattern options"
          >
            {frequencyPatterns
              .filter(pattern =>
                inputValue.trim().length === 0 || // Show all when empty (on focus)
                pattern.pattern.toLowerCase().includes(inputValue.toLowerCase()) ||
                pattern.description.toLowerCase().includes(inputValue.toLowerCase()) ||
                pattern.timing.toLowerCase().includes(inputValue.toLowerCase())
              )
              .map((pattern, index) => (
                <div
                  key={pattern.pattern}
                  className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    index === highlightedIndex 
                      ? 'bg-blue-100 text-blue-900' 
                      : 'hover:bg-gray-100'
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handlePatternSelect(pattern);
                  }}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  tabIndex={-1}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{pattern.pattern}</span>
                    <span className="text-xs text-gray-500">
                      {pattern.description} - {pattern.timing}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
} 